import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Mail, Send, FileDown } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SERIES, DECK_ROLES, DECK_INCLUDES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", email: "", role: "", message: "" };

const inputCls =
    "w-full border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-[#d6c68e] focus:bg-white/[0.06]";

export const RequestDeck = () => {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/deck-request`, form);
            setSent(true);
            setForm(EMPTY);
            toast.success("Request received — Celestial Light Studios will be in touch.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Something went wrong — please email us directly.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="request-deck-section" className="relative border-t border-white/10 bg-[#070c18]/70 py-24 sm:py-36 overflow-hidden">
            <div className="pointer-events-none absolute left-[-15%] bottom-[-20%] h-[520px] w-[520px] rounded-full bg-[#d6c68e]/10 blur-[170px]" />
            <div className="mx-auto grid max-w-[1500px] gap-16 px-5 sm:px-8 lg:grid-cols-2 lg:gap-24">
                <Reveal>
                    <Eyebrow>For Acquisitions, Sales Agents &amp; Platforms</Eyebrow>
                    <h2 className="deck-heading font-display text-6xl sm:text-7xl leading-[1.02] tracking-[0.1em]">
                        REQUEST<br />THE <span>DECK</span>
                    </h2>
                    <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-400">
                        The full pitch package for Deadly Ambition is available to industry. Tell us who you are and
                        where you're from — the deck follows by email.
                    </p>
                    <ul className="mt-8 space-y-3">
                        {DECK_INCLUDES.map((d) => (
                            <li key={d} className="flex items-center gap-3 text-sm text-zinc-300">
                                <Check size={15} className="shrink-0 text-[#d6c68e]" />
                                {d}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <a
                            data-testid="contact-direct-email"
                            href={`mailto:${SERIES.email}`}
                            className="inline-flex items-center gap-3 border border-white/15 px-5 py-3 font-mono text-[11px] tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:border-[#d6c68e] hover:text-[#d6c68e]"
                        >
                            <Mail size={14} />
                            {SERIES.email}
                        </a>
                        <a
                            data-testid="download-sell-sheet-btn"
                            href="/sell-sheet/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 border border-[#d6c68e] px-5 py-3 font-mono text-[11px] tracking-[0.2em] text-[#d6c68e] transition-all duration-300 hover:bg-[#d6c68e] hover:text-[#04060d]"
                        >
                            <FileDown size={14} />
                            ONE-PAGE SELL SHEET (PDF)
                        </a>
                    </div>
                </Reveal>

                <Reveal delay={0.12}>
                    {sent ? (
                        <div
                            data-testid="deck-form-success"
                            className="flex h-full min-h-[420px] flex-col items-center justify-center border border-[#d6c68e]/40 bg-[#0b1220] p-10 text-center"
                        >
                            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#d6c68e] text-[#d6c68e]">
                                <Check size={26} />
                            </span>
                            <h3 className="mt-6 font-display text-3xl tracking-[0.05em] text-white">REQUEST RECEIVED</h3>
                            <p className="mt-3 max-w-sm text-sm leading-relaxed text-zinc-400">
                                Thank you — your request has been sent to Celestial Light Studios. Expect the deck and
                                next steps by email.
                            </p>
                            <button
                                data-testid="deck-form-again-btn"
                                onClick={() => setSent(false)}
                                className="mt-8 font-mono text-[11px] tracking-[0.25em] text-zinc-500 underline underline-offset-4 transition-colors hover:text-[#d6c68e]"
                            >
                                SUBMIT ANOTHER REQUEST
                            </button>
                        </div>
                    ) : (
                        <form
                            data-testid="deck-request-form"
                            onSubmit={submit}
                            className="border border-white/10 bg-[#0b1220] p-7 sm:p-10"
                        >
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="deck-name" className="mb-2 block font-mono text-[10px] tracking-[0.25em] text-zinc-500">FULL NAME *</label>
                                    <input id="deck-name" data-testid="deck-form-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Doe" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="deck-company" className="mb-2 block font-mono text-[10px] tracking-[0.25em] text-zinc-500">COMPANY / PLATFORM *</label>
                                    <input id="deck-company" data-testid="deck-form-company-input" required value={form.company} onChange={set("company")} placeholder="Netflix, CAA, WME…" className={inputCls} />
                                </div>
                            </div>
                            <div className="mt-5 grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="deck-email" className="mb-2 block font-mono text-[10px] tracking-[0.25em] text-zinc-500">EMAIL *</label>
                                    <input id="deck-email" data-testid="deck-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="deck-role" className="mb-2 block font-mono text-[10px] tracking-[0.25em] text-zinc-500">ROLE *</label>
                                    <select id="deck-role" data-testid="deck-form-role-select" required value={form.role} onChange={set("role")} className={`${inputCls} appearance-none`}>
                                        <option value="" disabled className="bg-[#0b1220]">Select…</option>
                                        {DECK_ROLES.map((r) => (
                                            <option key={r} value={r} className="bg-[#0b1220]">{r}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="mt-5">
                                <label htmlFor="deck-message" className="mb-2 block font-mono text-[10px] tracking-[0.25em] text-zinc-500">MESSAGE</label>
                                <textarea id="deck-message" data-testid="deck-form-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us what you'd like to see…" className={`${inputCls} resize-none`} />
                            </div>
                            <button
                                data-testid="deck-form-submit-button"
                                type="submit"
                                disabled={sending}
                                className="mt-8 flex w-full items-center justify-center gap-3 bg-[#d6c68e] px-7 py-4 font-mono text-[11px] tracking-[0.28em] text-[#04060d] transition-all duration-300 hover:bg-[#f2e9c8] hover:shadow-[0_0_36px_rgba(214,198,142,0.45)] disabled:opacity-60 disabled:hover:shadow-none"
                            >
                                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />}
                                {sending ? "SENDING…" : "REQUEST THE DECK"}
                            </button>
                            <p className="mt-4 text-center font-mono text-[9px] tracking-[0.2em] text-zinc-600">
                                CONFIDENTIAL — FOR INDUSTRY EVALUATION ONLY
                            </p>
                        </form>
                    )}
                </Reveal>
            </div>
        </section>
    );
};
